import { useMutation } from '@apollo/client'
import {
    ShowGlobalSnackbarMutation,
    ShowGlobalSnackbarMutationProps,
    ShowGlobalSnackbarRequest,
} from '@deltek/specification-client/core/Configurations/GlobalSnackbar'
import { ContentItem } from '@deltek/specification-client/core/Models/contentItem'
import { InsertCustomManufacturerInput } from '@deltek/specification-client/core/Models/projectManufacturerProductListing'
import { pxToRem } from '@deltek/specification-client/core/Utilities/unitUtils'
import { makeStyles } from '@material-ui/core'
import { InsertCustomManufacturerInProductListingMutation } from 'api/graphQL/mutations/projectNugget.mutations'
import clsx from 'clsx'
import { useTrackChangesQuery } from 'components/WorkspaceEditor/components/TrackChangesPanel/api'
import React from 'react'
import type { ExistingCustomManufacturer } from './CustomManufacturerFieldsWrapper'

const PLACEHOLDER = 'Insert Manufacturers Name'
const MAX_FIELDS = 3
const INPUT_BORDER_COLOR = '#0D7B55'
const INACTIVE_COLOR = 'rgba(0,0,0,0.5)'
const EMPTY_MANUFACTURERS: ExistingCustomManufacturer[] = []

const useStyles = makeStyles((theme) => ({
    container: {
        display: 'block',
        marginTop: theme.spacing(0.5),
        paddingLeft: theme.spacing(5),
    },
    inputBox: {
        width: 180,
        fontSize: '1.4rem',
        background: '#FFFFFF',
        borderRadius: pxToRem(4),
        lineHeight: '16px',
        height: 'fit-content',
        outline: 'none',
        display: 'block',
        border: `1px solid ${INPUT_BORDER_COLOR}`,
        color: theme.palette.common.black,
        padding: theme.spacing(0.375, 0.5),
        marginBottom: theme.spacing(0.5),
        whiteSpace: 'nowrap',
        '&::placeholder': {
            color: 'rgba(0, 0, 0, 0.4)',
            fontStyle: 'italic',
        },
    },
    // Mirrors disabledOptionGroup + disabledBoxedInputContainer from useOptionGroupStyles —
    // same inactive treatment as ReplacementOption when its parent paragraph is hidden.
    inputBoxInactive: {
        textDecoration: 'line-through',
        textDecorationColor: `${INACTIVE_COLOR} !important`,
        color: `${INACTIVE_COLOR} !important`,
        border: `1px solid ${INACTIVE_COLOR}`,
        cursor: 'not-allowed',
        pointerEvents: 'none',
    },
}))

interface CustomManufacturerFieldsProps {
    elementId: string
    productFamilyId: string
    projectId: string
    existingCustomManufacturers?: ExistingCustomManufacturer[]
    onManufacturerAdded?: () => void
    isHidden?: boolean
}

export const CustomManufacturerFields: React.FC<CustomManufacturerFieldsProps> = ({
    elementId,
    productFamilyId,
    projectId,
    existingCustomManufacturers = EMPTY_MANUFACTURERS,
    onManufacturerAdded,
    isHidden = false,
}) => {
    const classes = useStyles()
    const { fetchTrackChangesDataWithApolloClient } = useTrackChangesQuery()

    const [fields, setFields] = React.useState<string[]>(['', '', ''])
    const hasChangesRef = React.useRef<boolean[]>([false, false, false])
    const fieldsRef = React.useRef<string[]>(['', '', ''])

    // Typed values keyed by pillId (for edits to a known existing pill) and/or
    // by `__slot_${index}` (for INSERTs whose pillId is not yet known).
    // Storing under both keys lets the typed value:
    //   • follow its pillId across alphabetical reorders (UPDATE path), and
    //   • survive when the pill is filtered out by parent paragraph
    //     activate/deactivate — Bug 2690577.
    // The insert mutation has no Apollo cache update, so the new pill never
    // reaches contentItemViewNodes from this path; the slot-key fallback is
    // the only way an INSERT-flow typed value remains visible.
    const userEditedValuesRef = React.useRef<Map<string, string>>(new Map())

    React.useEffect(() => {
        const newFields = Array.from({ length: MAX_FIELDS }, (_, i) => {
            const serverPill = existingCustomManufacturers[i]
            if (serverPill?.id) {
                return (
                    userEditedValuesRef.current.get(serverPill.id) ??
                    serverPill.name ??
                    ''
                )
            }
            return userEditedValuesRef.current.get(`__slot_${i}`) ?? ''
        })
        fieldsRef.current = newFields
        setFields(newFields)
    }, [existingCustomManufacturers])

    const [showGlobalSnackbar] = useMutation<
        ShowGlobalSnackbarRequest,
        ShowGlobalSnackbarMutationProps
    >(ShowGlobalSnackbarMutation)

    const [insertCustomManufacturerInProductListing] = useMutation<
        { insertCustomManufacturerInProductListing: ContentItem[] },
        { input: Partial<InsertCustomManufacturerInput> }
    >(InsertCustomManufacturerInProductListingMutation, {
        onError: (error) => {
            showGlobalSnackbar({
                variables: {
                    message: `There was a problem saving your changes. ${error}`,
                    variant: 'error',
                    id:
                        'WorkspaceEditor_InsertCustomManufacturerInProductListingError',
                },
            })
        },
        onCompleted: () => {
            onManufacturerAdded?.()
        },
    })

    const handleChange = React.useCallback(
        (index: number, value: string) => {
            setFields((prev) => {
                const next = [...prev]
                next[index] = value
                fieldsRef.current = next
                return next
            })
            hasChangesRef.current[index] = true
            const existing = existingCustomManufacturers[index]
            userEditedValuesRef.current.set(`__slot_${index}`, value)
            if (existing?.id) {
                userEditedValuesRef.current.set(existing.id, value)
            }
        },
        [existingCustomManufacturers]
    )

    const handleBlur = React.useCallback(
        async (index: number) => {
            if (!hasChangesRef.current[index]) {
                return
            }

            const existing = existingCustomManufacturers[index]
            const manufacturerName = fieldsRef.current[index]?.trim() ?? ''

            hasChangesRef.current[index] = false

            try {
                await insertCustomManufacturerInProductListing({
                    variables: {
                        input: {
                            elementId,
                            isBOD: false,
                            manufacturerName,
                            productFamilyId,
                            projectId,
                            ...(existing?.id
                                ? { customManufacturerProductId: existing.id }
                                : {}),
                        },
                    },
                })
                // *WithApolloClient variant is documented in useTrackChangesQuery
                // as the correct choice inside mutation callbacks (promise-based,
                // independent of React render cycles).
                await fetchTrackChangesDataWithApolloClient()
            } catch {
                // onError on the mutation hook already surfaces the snackbar.
            }
        },
        [
            existingCustomManufacturers,
            elementId,
            productFamilyId,
            projectId,
            insertCustomManufacturerInProductListing,
            fetchTrackChangesDataWithApolloClient,
        ]
    )

    return (
        <div className={classes.container}>
            {fields.map((value, index) => (
                <input
                    key={index}
                    data-testid={`custom-manufacturer-${index + 1}`}
                    className={clsx(
                        classes.inputBox,
                        isHidden && classes.inputBoxInactive
                    )}
                    placeholder={PLACEHOLDER}
                    value={value}
                    disabled={isHidden}
                    onChange={(e) => handleChange(index, e.target.value)}
                    onBlur={() => handleBlur(index)}
                />
            ))}
        </div>
    )
}
